<template>
  <form @submit.prevent="handleSubmit" class="space-y-4 h-[60vh] overflow-auto pr-2">
    <!-- Static Fields Section -->
    <div>
  <!-- <h2 class="text-xl font-bold text-gray-800 mb-4">Rate your experience</h2> -->
  <div
    v-for="(field, index) in defaultRatingFields"
    :key="'static-' + index"
    class="bg-gray-100 p-4 rounded-lg mb-4"
  >
    <div class="flex items-center gap-4">
      <img
       :src="field.image"
       :alt="field.name"
       :title="field.name"
       class="w-12 h-12 rounded-full object-contain border border-gray-300 bg-white"
      />

      <div class="flex-1">
        <h3 class="text-base font-semibold capitalize mb-1 text-gray-800">
          {{ field.name }}
        </h3>
        <V3StarRating v-model="ratings[field.name]" />
      </div>
    </div>
  </div>
</div>

    <!-- Item Ratings Section -->
    <div>
      <h2 class="text-xl font-bold mb-4">Rate Your Order</h2>
      <div
        v-for="(item, index) in ratingItems"
        :key="'item-' + index"
        class="bg-gray-100 p-4 rounded-lg mb-4"
      >
        <div class="flex items-center gap-4">
          <NuxtImg
            :src="useSecureImage(item.images.thumbnail)"
            class="w-12 h-12 rounded-full object-contain border border-gray-300 bg-white"
            @error="setDefaultImage"
          />

          <div class="flex-1">
            <h3 class="text-base font-semibold capitalize mb-1 text-gray-800">
              {{ item.name }}
            </h3>
            <V3StarRating v-model="ratings[item.name]" />
          </div>
        </div>
      </div>
    </div>

    <p v-if="showMessage" class="text-center text-red-500">
      Please select a rating
    </p>
    <div class="flex justify-center">
      <UButton size="xl" class="tracking-wide w-full md:w-auto text-sm md:text-md" type="submit" :disabled="disable">
        Submit
      </UButton>
    </div>
  </form>
</template>

<script setup>
import { ref } from 'vue'
import { V3StarRating } from '#components'
import { EventParameter } from '~/google_analytics/events.enum';
import { GoogleAnalytics } from '~/google_analytics/events';
const props = defineProps(['data']);
const order = props.data.orderData;
const emit = defineEmits(['success', 'close']);
const defaultRatingFields = [
  {
    name: 'Overall shopping experience',
    image: '/v1/icons/overall_rating.jpeg',
  },
  {
    name: 'Seller experience',
    image: '/v1/icons/seller_rating.jpeg',
  },
  {
    name: 'Delivery experience',
    image: '/v1/icons/delivery_rating.jpeg',
  },
]
const myOrdersTab = props.data?.currentTab;
const ratingItems = order.products;
const showMessage = ref(false);
const runtimeConfig = useRuntimeConfig() 
const setDefaultImage = (event) => {
  event.imageSrc = runtimeConfig.public.defaultProductImage;
}
const SUCCESS_MESSAGE_KEY = 'showSuccessMessage';
const disable = ref(false);
const ratings = ref({})

for (const field of defaultRatingFields) {
  ratings.value[field.name] = 0
}

for (const item of ratingItems) {
  ratings.value[item.name] = 0
}

const user = {
  _id: order.user._id,
  first_name: order.user.first_name,
  last_name: order.user.last_name,
  phone: order.user.phone,
  email: order.user.email ?? '',
}

const provider_id = order.provider.id;
const auth = useAuth();
const { gtag } = useGtag();
const isAuthenticated = auth && auth.access_token;
const userData = isAuthenticated
  ? {
      user_name: auth.user.first_name,
      user_phone: auth.user.phone,
      user_email: auth.user.email,
    }
  : {
      user_name: null,
      user_phone: null,
      user_email: null,
    };
const handleSubmit = async () => {
  const result = []

  for (const [field, value] of Object.entries(ratings.value)) {
    if (!value) continue // Skip if no rating selected

    const base = {
      value: value.toString(),
      user,
      order_id: order.order_id,
      transaction_id: order.transaction_id,
    }

    switch (field.toLowerCase()) {
      case 'overall shopping experience':
        result.push({
          ...base,
          id: order.order_id,
          rating_category: "order",
          provider_id
        })
        break

      case 'seller experience':
        result.push({
          ...base,
          id: provider_id,
          rating_category: "provider"
        })
        break

      case 'delivery experience':
        result.push({
          ...base,
          id: order.message.order.fulfillments[0].id,
          rating_category: "fulfillment",
          provider_id
        })
        break

      default:
        const matchedItem = ratingItems.find(item => item.name === field)
        if (matchedItem) {
          result.push({
            ...base,
            id: matchedItem.item_id,
            rating_category: "item",
            provider_id
          })
        }
        break
    }
  }

  if(result.length < 1) {
    disable.value = true;
    showMessage.value = true;
    return;
  }

  const response = await useAPIHandler(
    "post",
    "/ratings/submit",
    {
      body: result,
      auth: true
    }
  );

  if(response.data.status == 'Success') {
    emit("success");
    localStorage.setItem(SUCCESS_MESSAGE_KEY, 'true');

    if(myOrdersTab) {
      localStorage.setItem('myOrdersTab', myOrdersTab);
    }

    GoogleAnalytics.cta({
      [EventParameter.CTA_NAME]: 'rating_submit'
    });
  }
}

watch(
  ratings,
  (newVal, oldVal) => {
    if(newVal) {
      disable.value = false;
      showMessage.value = false;
    }
  },
  { deep: true }
);
</script>